// Copyright (c) Bentley Systems
import { ComponentProps, ReactNode, useMemo } from "react";
import {
  DropdownMenu,
  ExpandableBlock,
  IconButton,
  Text,
  Tooltip,
} from "@itwin/itwinui-react";
import { SvgMore } from "@itwin/itwinui-icons-react";
import { FormatDate } from "../../Helpers/helper-functions";
import OnPremHealthStatusBadge from "../../OnPremTelemetry/OnPremHealthStatusBadge";
import OnPremVersionBadge from "../../OnPremTelemetry/OnPremVersionBadge";
import ProjectRunsManagementTable from "../../OnPremTelemetry/ProjectRunsManagementTable";
import {
  ClientTelemetryOverview,
  dayFilterLabel,
  DayFilterValue,
  toClientProjectRows,
} from "../../OnPremTelemetry/Models/on-prem-telemetry-models";
import { OnPremise } from "./Models/on-premise";

export const ON_PREM_HEALTH_WINDOW: DayFilterValue = 7;

type OnPremClientBlockProps = {
  onPrem: OnPremise;
  /** The client's telemetry within the current iTwin; absent when it has no runs there. */
  telemetry?: ClientTelemetryOverview;
  /** False where the telemetry endpoint is unavailable, which hides every telemetry element. */
  isTelemetryAvailable: boolean;
  menuItems: ComponentProps<typeof DropdownMenu>["menuItems"];
  /** The client's systems section: its actions and systems table. */
  children: ReactNode;
};

/** One registered on-prem client on the credentials page: its details, systems, and recent runs in the iTwin. */
const OnPremClientBlock = ({
  onPrem,
  telemetry,
  isTelemetryAvailable,
  menuItems,
  children,
}: OnPremClientBlockProps) => {
  const projects = useMemo(() => telemetry?.projects ?? [], [telemetry]);
  // Stable rows: the runs table resets its selection whenever `rows` changes identity.
  const recentRunRows = useMemo(
    () => toClientProjectRows(onPrem.mqttClientId, projects, ON_PREM_HEALTH_WINDOW),
    [onPrem.mqttClientId, projects],
  );

  return (
    <ExpandableBlock.Wrapper>
      <ExpandableBlock.Trigger>
        <ExpandableBlock.ExpandIcon />
        <ExpandableBlock.LabelArea>
          <ExpandableBlock.Title>
            <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
              <Text>
                <b>{onPrem.name}</b>
              </Text>
              {isTelemetryAvailable && (
                <>
                  <OnPremHealthStatusBadge projects={projects} dayFilter={ON_PREM_HEALTH_WINDOW} />
                  <OnPremVersionBadge version={telemetry?.installedVersion} iTwinScoped />
                </>
              )}
            </div>
          </ExpandableBlock.Title>
          <ExpandableBlock.Caption>
            <div className="container">
              <div className="column left">
                MqttClientId: <b>{onPrem.mqttClientId}</b>
              </div>
              <div className="column center">
                Last change: <b>{FormatDate(onPrem.lastChange)}</b>
              </div>
              <div className="column right">
                Changed by: <b>{onPrem.lastChangeByName}</b>
              </div>
              {isTelemetryAvailable && (
                <div className="column center">
                  <Tooltip content="When this on-prem client last returned a run result, in any iTwin.">
                    <span>
                      Last used:{" "}
                      <b>{telemetry?.lastUsed ? FormatDate(new Date(telemetry.lastUsed)) : "—"}</b>
                    </span>
                  </Tooltip>
                </div>
              )}
              <DropdownMenu menuItems={menuItems}>
                <IconButton
                  style={{
                    marginLeft: "var(--iui-size-s)",
                  }}
                  label="More options"
                  styleType="borderless"
                  size="small"
                >
                  <SvgMore />
                </IconButton>
              </DropdownMenu>
            </div>
          </ExpandableBlock.Caption>
        </ExpandableBlock.LabelArea>
      </ExpandableBlock.Trigger>
      <ExpandableBlock.Content>
        <div>
          {children}
          {isTelemetryAvailable && (
            <div className="becs-tb-margin">
              <Text variant="leading">
                Projects run in the {dayFilterLabel(ON_PREM_HEALTH_WINDOW).toLowerCase()}
              </Text>
              <Text isMuted>
                Each integration project is listed once, with its latest run on
                this client in this iTwin. The health status is based on these
                projects.
              </Text>
              <ProjectRunsManagementTable rows={recentRunRows} />
            </div>
          )}
        </div>
      </ExpandableBlock.Content>
    </ExpandableBlock.Wrapper>
  );
};

export default OnPremClientBlock;
